import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { updateRatingError } from '@acorn-portal/shared/src/store/cancellation/cancellation.slice';
import { useEffect, useMemo } from 'react';
import CancellationDateForm from './components/form/cancellation-date-form';
import Heading from './components/heading';
import ErrorModal from './components/modal/error-modal';
import JourneySwitchModals from './components/modal/journey-switch';
import ContentWrapper from './components/wrapper/content-wrapper';
import PageWrapper from './components/wrapper/page-wrapper';
import { useRatingSubmission } from './hooks/use-rating-submission';
import { getStaticDropdownOptions } from './utils/form-dropdowns/date-form-dropdowns/get-static-dropdown-options';
import {
	CancellationDateFormValues,
	getInitialCancellationDateFormValues,
} from './utils/get-initial-cancellation-date-form-values';
import cancellationDateSchema from './validation/cancellation-date-schema';
import { getPhoneNumber } from '../../utils/get-phone-numbers';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

const CancellationDate = () => {
	// Store
	const dispatch = useAppDispatch();
	const cancellation = useAppSelector((state) => state.cancellation);
	const product = useAppSelector((state) => state.fullPolicy.product);
	// Form
	const initialValues = getInitialCancellationDateFormValues(cancellation.cancellationDate);
	// Dropdowns
	const staticOptions = useMemo(() => getStaticDropdownOptions({ minuteWindow: 15 }), []); // date and description
	// Submission
	const { handleSubmit } = useRatingSubmission();
	// Error handling
	const contactNumber = getPhoneNumber(product, 'CONTACT');
	const resetStoreError = () => {
		dispatch(updateRatingError(undefined)); // close the modal
	};

	const { track } = useTrack();

	useEffect(() => {
		track('cancellation_step_2_started', { name: 'cancellation_date_page' });
	}, []);

	const onSubmit = (values: CancellationDateFormValues) => {
		handleSubmit(values);
		track('cancellation_date_submitted', { step_number: 2, ...values });
	};

	const onBack = () => {
		track('cancellation_date_back_button', { step_number: 2 });
	};

	return (
		<>
			{/* Popups that appear on page land if alternatives to cancellation are possible */}
			<JourneySwitchModals />
			<LoadingModal open={cancellation.ratingStatus.isLoading} message='Loading' />
			{/* Main page content */}
			<PageWrapper>
				<ContentWrapper>
					<Heading>Cancellation date</Heading>
					{/* Cancellation Date form */}
					<CancellationDateForm
						initialValues={initialValues}
						validationSchema={cancellationDateSchema}
						handleSubmit={onSubmit}
						staticOptions={staticOptions}
						timeDropDownConfig={{ minuteIncrement: 15, hidePastTimes: true }}
						onBack={onBack}
					/>
					{/* Error Modal - driven by a rating error */}
					<ErrorModal
						open={!!cancellation.ratingStatus.error}
						onClose={resetStoreError}
						contactNumber={contactNumber}
					/>
				</ContentWrapper>
			</PageWrapper>
		</>
	);
};

export default CancellationDate;
